// @ts-nocheck
/**
 * BaseOrderGrid — Shared AG Grid component for Order By SKU and Order By Store
 *
 * Accepts configuration via props (column config, group options, metrics, editing rules).
 * Both agOrderGrid.tsx (SKU page) and agStoreOrderGrid.tsx (Store page) are thin wrappers.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
	ModuleRegistry,
	ClientSideRowModelModule,
	RowApiModule,
	ColumnApiModule,
	TextEditorModule,
	NumberEditorModule,
	CustomFilterModule,
	ColumnAutoSizeModule,
	CustomEditorModule,
	CellStyleModule,
	TextFilterModule,
	RowStyleModule,
	RenderApiModule,
	NumberFilterModule,
	RowSelectionModule,
	themeQuartz,
} from 'ag-grid-community';
import {
	AggregationModule,
	ColumnMenuModule,
	ColumnsToolPanelModule,
	ContextMenuModule,
	FiltersToolPanelModule,
	RowGroupingModule,
	RowGroupingPanelModule,
	SetFilterModule,
	ClipboardModule,
	TooltipModule,
} from 'ag-grid-enterprise';

import { Bookmarks } from 'molecules/ReactGrid/agGrid/bookmarks/bookmarks';
import {
	GRID_HEADER_HEIGHT,
	GRID_ROW_HEIGHT,
	GRID_FONT_SIZE,
	GRID_MIN_COL_WIDTH,
	AVAILABILITY_CAP_ERROR_MS,
} from '../constants';

// Register modules once — ValidationModule deliberately excluded (performance).
ModuleRegistry.registerModules([
	ClientSideRowModelModule,
	ClipboardModule,
	RenderApiModule,
	RowStyleModule,
	TextFilterModule,
	NumberFilterModule,
	SetFilterModule,
	ColumnsToolPanelModule,
	ColumnMenuModule,
	ContextMenuModule,
	FiltersToolPanelModule,
	TooltipModule,
	RowGroupingModule,
	RowGroupingPanelModule,
	RowApiModule,
	ColumnApiModule,
	TextEditorModule,
	NumberEditorModule,
	CustomFilterModule,
	ColumnAutoSizeModule,
	CustomEditorModule,
	CellStyleModule,
	RowSelectionModule,
	AggregationModule,
]);

/* ─── Field detection helpers ─── */
const isCurrencyField = (f: string) => /Retail|Price|Cost|Amt$/i.test(f);
const isPercentField = (f: string) => /Per$|Margin/i.test(f);
const isNumericField = (f: string) =>
	/Qty|OnHand|InTransit|OnOrder|WOS|Sales|Forecast|Demand|Shipped|Days|Storage|Conversion|Gap/i.test(f) ||
	isCurrencyField(f) ||
	isPercentField(f);

/** Convert a camelCase/PascalCase field name to a readable header */
const fieldToHeader = (field: string): string =>
	field
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.replace(/^./, (s) => s.toUpperCase());

export interface MetricConfig {
	label: string;
	valueFn: (data: Record<string, any>[]) => number;
	color: string;
}

export interface MetricsGroup {
	groupName: string;
	headerClass?: string;
	columns: string[];
	defaultHidden?: boolean;
}

export interface BaseOrderGridProps {
	data: Record<string, any>[];
	colConfig: any[];
	groupOptions: { field: string; label: string }[];
	excludedFields?: Set<string>;
	metricsColumnGroups?: MetricsGroup[];

	// OrderQty editing
	onOrderQtyChange?: (rowIdx: number, entityId: string, newQty: number) => void;
	entityIdField?: string;
	shelfQty?: number;
	shelfQtyField?: string;
	availabilityQty?: number;

	// Row selection
	selectionMode?: 'single' | 'multiRow';
	onSelectionChanged?: (rows: Record<string, any>[]) => void;

	// Metrics bar
	metricsConfig?: MetricConfig[];

	// Groupable columns from parent filters
	groupableColumns?: { key: string; label: string }[];

	// Grid API ref
	gridApiRef?: React.MutableRefObject<any>;

	// Extra defaultColDef overrides
	defaultColDefOverrides?: Record<string, any>;

	// Table ID for bookmark integration
	tableId?: number;
}

const BaseOrderGrid: React.FC<BaseOrderGridProps> = ({
	data,
	colConfig,
	groupOptions: baseGroupOptions,
	excludedFields,
	metricsColumnGroups,
	onOrderQtyChange,
	entityIdField = 'Store',
	shelfQty: globalShelfQty,
	shelfQtyField,
	availabilityQty,
	selectionMode = 'single',
	onSelectionChanged: onSelectionChangedProp,
	metricsConfig,
	groupableColumns,
	gridApiRef,
	defaultColDefOverrides,
	tableId,
}) => {
	const gridRef = useRef<AgGridReact>(null);
	const [activeGroup, setActiveGroup] = useState<string | null>(null);
	const [activeFilters, setActiveFilters] = useState<{ field: string; value: string }[]>([]);
	const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
	const savedColumnStateRef = useRef<any[] | null>(null);
	const hasAppliedInitialSizing = useRef(false);
	const groupBtnRef = useRef<HTMLDivElement>(null);

	/* Ref to current data — allows stable callbacks to read fresh data without re-creation */
	const dataRef = useRef(data);
	dataRef.current = data;

	/* Close group dropdown on outside click */
	useEffect(() => {
		if (!groupDropdownOpen) return;
		const handler = (e: MouseEvent) => {
			if (groupBtnRef.current && !groupBtnRef.current.contains(e.target as Node)) {
				setGroupDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [groupDropdownOpen]);

	/* Stable key for data schema — only changes when the set of fields changes */
	const dataFieldsKey = useMemo(() => {
		if (!data?.length) return '';
		return Object.keys(data[0]).sort().join(',');
	}, [data]);

	/* Precompute COL_CONFIG field set for auto-discovery exclusion */
	const colConfigFields = useMemo(() => new Set(colConfig.map((c) => c.field)), [colConfig]);

	/* Resolve effective shelf qty for a given row — per-row field takes priority over global fallback */
	const getShelfStep = useCallback(
		(rowData?: any) => {
			if (shelfQtyField && rowData) {
				const v = Number(rowData[shelfQtyField] || 0);
				if (v > 1) return v;
			}
			if (globalShelfQty != null && globalShelfQty > 1) return globalShelfQty;
			return 1;
		},
		[globalShelfQty, shelfQtyField]
	);

	/* Build column defs */
	const columnDefs = useMemo(() => {
		if (!dataFieldsKey) return colConfig;
		const dataKeys = new Set(dataFieldsKey.split(','));

		/* 1) Curated columns (only those present in data) — add aggFunc for numeric cols */
		const baseCols: any[] = colConfig
			.filter((c) => dataKeys.has(c.field))
			.map((c) => {
				const col = { ...c };
				if (isNumericField(c.field) && !col.aggFunc) {
					if (isPercentField(c.field) || /WOS/i.test(c.field)) {
						col.aggFunc = 'avg';
					} else {
						col.aggFunc = 'sum';
					}
				}
				return col;
			});

		/* 2) Dynamic OrderQty column — step is per-row via cellEditorParams function */
		if (dataKeys.has('OrderQty')) {
			const orderQtyCol: any = {
				field: 'OrderQty',
				headerName: 'Order Qty',
				headerTooltip: shelfQtyField
					? 'Arrow keys step by shelf qty. Values snap to shelf multiples.'
					: globalShelfQty && globalShelfQty > 1
						? `Arrow keys step by ${globalShelfQty}. Values snap to shelf multiples.`
						: 'Order Quantity',
				width: 110,
				type: 'numericColumn',
				editable: (params) => !params.data?.IsReadOnly,
				cellEditor: 'agNumberCellEditor',
				cellEditorParams: (params) => {
					let s = 1;
					if (shelfQtyField && params.data) {
						const v = Number(params.data[shelfQtyField] || 0);
						if (v > 1) s = v;
					}
					if (s === 1 && globalShelfQty != null && globalShelfQty > 1) s = globalShelfQty;
					return { min: 0, step: s, precision: 0 };
				},
				aggFunc: 'sum',
			};

			/* Only add availability-aware styling if availabilityQty is supported */
			if (availabilityQty != null) {
				orderQtyCol.cellClass = (params) => {
					if (params.data?.IsReadOnly) return 'obs-orderqty-readonly';
					if (params.data?._availError) return 'obs-orderqty-error';
					return 'obs-orderqty';
				};
				orderQtyCol.cellStyle = (params) =>
					params.data?.IsReadOnly ? { cursor: 'not-allowed' } : { cursor: 'pointer' };
				orderQtyCol.cellRenderer = (params) =>
					params.data?.IsReadOnly ? `🔒 ${params.value ?? ''}` : (params.value ?? '');
				orderQtyCol.tooltipValueGetter = (params) => {
					if (params.data?.IsReadOnly) return 'This row is read-only';
					return params.data?._availError || null;
				};
			} else {
				/* Simple styling for Store page */
				orderQtyCol.cellStyle = { backgroundColor: '#fffde7', cursor: 'pointer' };
				orderQtyCol.editable = true;
			}

			baseCols.push(orderQtyCol);
		}

		/* 3) Auto-discover remaining data fields if excludedFields is provided */
		if (excludedFields) {
			dataKeys.forEach((field) => {
				if (colConfigFields.has(field) || excludedFields.has(field) || field === 'OrderQty') return;

				const col: any = {
					field,
					headerName: fieldToHeader(field),
					hide: true,
					enableRowGroup: true,
				};

				if (isNumericField(field)) {
					col.type = 'numericColumn';
					col.aggFunc = 'sum';
					if (isCurrencyField(field)) {
						col.valueFormatter = (p: any) => (p.value != null ? '$' + Number(p.value).toFixed(2) : '');
						col.width = 95;
					} else if (isPercentField(field)) {
						col.valueFormatter = (p: any) => (p.value != null ? Number(p.value).toFixed(1) + '%' : '');
						col.width = 80;
						col.aggFunc = 'avg'; // Percentages should average, not sum
					} else if (/WOS/i.test(field)) {
						col.valueFormatter = (p: any) => (p.value != null ? Number(p.value).toFixed(1) : '');
						col.width = 75;
						col.aggFunc = 'avg'; // Weeks of supply should average, not sum
					} else {
						col.width = 85;
					}
				} else if (/Date/i.test(field)) {
					col.width = 100;
				} else {
					col.width = 120;
				}

				baseCols.push(col);
			});
		}

		return baseCols;
	}, [dataFieldsKey, globalShelfQty, shelfQtyField, colConfig, colConfigFields, excludedFields, availabilityQty]);

	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			resizable: true,
			floatingFilter: false,
			suppressHeaderMenuButton: true,
			suppressHeaderContextMenu: true,
			minWidth: GRID_MIN_COL_WIDTH,
			...defaultColDefOverrides,
		}),
		[defaultColDefOverrides]
	);

	/* Column tool panel layout — optional, driven by metricsColumnGroups */
	const columnToolPanelLayout = useMemo(() => {
		if (!metricsColumnGroups || !columnDefs?.length) return undefined;

		const allFields = new Set(columnDefs.map((c: any) => c.field).filter(Boolean));
		const groupedFields = new Set<string>();
		const groups: any[] = [];

		for (const mg of metricsColumnGroups) {
			const children = mg.columns
				.filter((f) => allFields.has(f) && !excludedFields?.has(f))
				.map((f) => ({ columnId: f }));
			if (children.length > 0) {
				groups.push({ headerName: mg.groupName, children });
				children.forEach((c) => groupedFields.add(c.columnId));
			}
		}

		const ungrouped = columnDefs
			.filter((c: any) => c.field && !groupedFields.has(c.field))
			.map((c: any) => ({ columnId: c.field }));

		return [...ungrouped, ...groups];
	}, [columnDefs, metricsColumnGroups, excludedFields]);

	/* Metrics bar */
	const metrics = useMemo(() => {
		if (!metricsConfig || !data?.length) return metricsConfig?.map((m) => ({ ...m, value: 0 })) || [];
		return metricsConfig.map((m) => ({ ...m, value: m.valueFn(data) }));
	}, [data, metricsConfig]);

	/* Handle cell edit — snap to shelf multiple, optional availability cap */
	const onCellValueChanged = useCallback(
		(params) => {
			if (params.colDef.field !== 'OrderQty') return;
			const step = getShelfStep(params.data);
			const raw = Number(params.newValue || 0);

			let qty = raw > 0 ? Math.ceil(raw / step) * step : 0;

			/* Availability cap (only when availabilityQty is provided) */
			const snappedQty = qty;
			if (availabilityQty != null && qty > 0) {
				let otherTotal = 0;
				const currentRowId = params.data?.OrderDetailID;
				const currentData = dataRef.current || [];
				for (let i = 0; i < currentData.length; i++) {
					if (currentData[i].OrderDetailID !== currentRowId) {
						otherTotal += Number(currentData[i].OrderQty || 0);
					}
				}
				const maxForRow = Math.max(0, availabilityQty - otherTotal);
				if (qty > maxForRow) {
					qty = Math.floor(maxForRow / step) * step;
					if (qty < 0) qty = 0;
				}
			}

			const wasCapped = qty < snappedQty;

			params.data.OrderQty = qty;
			if (wasCapped) {
				const maxAvail = availabilityQty ?? 0;
				params.data._availError = `Qty capped at ${qty} — only ${maxAvail} units available across all stores`;
				params.api.refreshCells({ rowNodes: [params.node], columns: ['OrderQty'], force: true });
				setTimeout(() => {
					if (params.data._availError) {
						delete params.data._availError;
						try {
							params.api.refreshCells({ rowNodes: [params.node], columns: ['OrderQty'], force: true });
						} catch (e) {}
					}
				}, AVAILABILITY_CAP_ERROR_MS);
			} else if (qty !== raw) {
				params.api.refreshCells({ rowNodes: [params.node], columns: ['OrderQty'], force: true });
			}

			const entityId = String(params.data?.[entityIdField] || '');
			onOrderQtyChange?.(params.rowIndex, entityId, qty);
		},
		[onOrderQtyChange, getShelfStep, availabilityQty, entityIdField]
	);

	const onGridReady = useCallback(
		(params) => {
			if (gridApiRef) gridApiRef.current = params.api;
		},
		[gridApiRef]
	);

	const handleSelectionChanged = useCallback(() => {
		if (!gridRef.current?.api || !onSelectionChangedProp) return;
		const rows = gridRef.current.api.getSelectedRows();
		onSelectionChangedProp(rows);
	}, [onSelectionChangedProp]);

	/* Initial auto-size */
	const onFirstDataRendered = useCallback(() => {
		if (!hasAppliedInitialSizing.current && gridRef.current?.api) {
			gridRef.current.api.sizeColumnsToFit();
			hasAppliedInitialSizing.current = true;
		}
	}, []);

	/* Column state persistence (debounced via rAF) */
	const colStateTimer = useRef<any>(null);
	const onColumnStateChanged = useCallback(() => {
		if (colStateTimer.current) cancelAnimationFrame(colStateTimer.current);
		colStateTimer.current = requestAnimationFrame(() => {
			if (!gridRef.current?.api) return;
			savedColumnStateRef.current = gridRef.current.api.getColumnState();
		});
	}, []);

	/* Quick group handler — imperative API update */
	const handleGroup = useCallback(
		(field: string | null) => {
			setActiveGroup((prev) => {
				const newGroup = prev === field ? null : field;
				const api = gridRef.current?.api;
				if (!api) return newGroup;

				const colState = api.getColumnState();
				const updates = colState.map((cs) => ({
					colId: cs.colId,
					rowGroup: newGroup ? cs.colId === newGroup : false,
					hide:
						newGroup && cs.colId === newGroup
							? true
							: cs.colId === prev
								? colConfig.find((c) => c.field === cs.colId)?.hide || false
								: cs.hide,
				}));
				api.applyColumnState({ state: updates });
				return newGroup;
			});
			setGroupDropdownOpen(false);
		},
		[colConfig]
	);

	/* Filter change tracking */
	const onFilterChanged = useCallback(() => {
		if (!gridRef.current?.api) return;
		const model = gridRef.current.api.getFilterModel();
		const tags = [];
		Object.entries(model).forEach(([field, filter]: [string, any]) => {
			const col = colConfig.find((c) => c.field === field);
			const label = col?.headerName || field;
			if (filter.values) {
				tags.push({ field: label, value: filter.values.join(', ') });
			} else if (filter.filter != null) {
				tags.push({ field: label, value: `${filter.type || ''} ${filter.filter}` });
			} else {
				tags.push({ field: label, value: 'filtered' });
			}
		});
		setActiveFilters(tags);
	}, [colConfig]);

	const clearFilter = useCallback(
		(fieldLabel: string) => {
			if (!gridRef.current?.api) return;
			const col = colConfig.find((c) => c.headerName === fieldLabel);
			if (col) gridRef.current.api.destroyFilter(col.field);
		},
		[colConfig]
	);

	const clearAllFilters = useCallback(() => {
		if (!gridRef.current?.api) return;
		gridRef.current.api.setFilterModel(null);
	}, []);

	const theme = useMemo(
		() =>
			themeQuartz.withParams({
				headerHeight: GRID_HEADER_HEIGHT,
				rowHeight: GRID_ROW_HEIGHT,
				fontSize: GRID_FONT_SIZE,
			}),
		[]
	);

	/* Build available group options from base + groupableColumns prop */
	const groupOptions = useMemo(() => {
		const opts = [...baseGroupOptions];
		if (groupableColumns?.length) {
			groupableColumns.forEach((gc) => {
				if (!opts.find((o) => o.field === gc.key)) {
					opts.push({ field: gc.key, label: gc.label });
				}
			});
		}
		if (!dataFieldsKey) return opts;
		const dataKeys = new Set(dataFieldsKey.split(','));
		return opts.filter((o) => dataKeys.has(o.field));
	}, [dataFieldsKey, groupableColumns, baseGroupOptions]);

	/* Stable unique row ID */
	const getRowId = useCallback(
		(params) => {
			const d = params.data;
			if (!d) return String(params.rowIndex);
			return String(d.OrderDetailID ?? `${d[entityIdField] ?? ''}_${d.Warehouse ?? ''}_${params.rowIndex}`);
		},
		[entityIdField]
	);

	/* Memoized AG Grid config objects */
	const rowSelection = useMemo(() => {
		if (selectionMode === 'multiRow') {
			return { mode: 'multiRow', checkboxes: true, headerCheckbox: true, enableClickSelection: false };
		}
		return 'single';
	}, [selectionMode]);

	const selectionColumnDef = useMemo(
		() =>
			selectionMode === 'multiRow'
				? { pinned: 'left', width: 40, maxWidth: 40, suppressHeaderMenuButton: true }
				: undefined,
		[selectionMode]
	);

	const sideBarConfig = useMemo(
		() => ({
			toolPanels: [
				{
					id: 'columns',
					labelDefault: 'Columns',
					labelKey: 'columns',
					iconKey: 'columns',
					toolPanel: 'agColumnsToolPanel',
					toolPanelParams: {
						suppressPivotMode: true,
						suppressPivots: true,
						suppressValues: true,
						suppressRowGroups: true,
						...(columnToolPanelLayout ? { columnLayout: columnToolPanelLayout } : {}),
					},
				},
				{
					id: 'filters',
					labelDefault: 'Filters',
					labelKey: 'filters',
					iconKey: 'filter',
					toolPanel: 'agFiltersToolPanel',
				},
				...(tableId != null
					? [
							{
								id: 'bookmarksPanel',
								labelDefault: 'Bookmarks',
								labelKey: 'bookmarksPanel',
								iconKey: 'menu',
								toolPanel: Bookmarks,
								toolPanelParams: { tableId: String(tableId) },
							},
						]
					: []),
			],
			defaultToolPanel: '',
		}),
		[columnToolPanelLayout, tableId]
	);

	const autoGroupColDef = useMemo(
		() => ({
			headerName: 'Group',
			minWidth: 220,
			cellRendererParams: { suppressCount: false },
			pinned: 'left',
		}),
		[]
	);

	return (
		<div className="ag-order-grid-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			{/* Metrics bar */}
			{metrics.length > 0 && (
				<div className="obs-metrics-bar">
					{metrics.map((m) => (
						<div key={m.label} className="obs-metrics-bar__card">
							<div className="obs-metrics-bar__value" style={{ color: m.color }}>
								{m.value.toLocaleString()}
							</div>
							<div className="obs-metrics-bar__label">{m.label}</div>
						</div>
					))}
				</div>
			)}

			{/* Toolbar */}
			<div className="obs-toolbar">
				{activeGroup &&
					(() => {
						const opt = groupOptions.find((o) => o.field === activeGroup);
						return (
							<span className="obs-toolbar__group-tag">
								Grouped: {opt?.label || activeGroup}
								<span onClick={() => handleGroup(null)} className="obs-toolbar__group-tag-x">
									&times;
								</span>
							</span>
						);
					})()}

				<span style={{ flex: 1 }} />

				<div ref={groupBtnRef} style={{ position: 'relative' }}>
					<button
						onClick={() => setGroupDropdownOpen((prev) => !prev)}
						className={`obs-toolbar__btn ${activeGroup ? 'obs-toolbar__btn--active' : ''}`}
						title="Group rows by column"
					>
						Group ▾
					</button>
					{groupDropdownOpen && (
						<div className="obs-toolbar__dropdown">
							<div
								onClick={() => handleGroup(null)}
								className={`obs-toolbar__dropdown-item ${!activeGroup ? 'obs-toolbar__dropdown-item--active' : ''}`}
							>
								None (no grouping)
							</div>
							{groupOptions.map((opt) => (
								<div
									key={opt.field}
									onClick={() => handleGroup(opt.field)}
									className={`obs-toolbar__dropdown-item ${activeGroup === opt.field ? 'obs-toolbar__dropdown-item--active' : ''}`}
								>
									{opt.label}
								</div>
							))}
						</div>
					)}
				</div>

				<button
					onClick={() => {
						const api = gridRef.current?.api;
						if (api) api.autoSizeAllColumns();
					}}
					className="obs-toolbar__btn"
					title="Autosize all columns"
				>
					Autosize
				</button>
			</div>

			{/* Active filter tags */}
			{activeFilters.length > 0 && (
				<div className="obs-filter-bar">
					<span className="obs-filter-bar__label">Filters:</span>
					{activeFilters.map((f, i) => (
						<span key={i} className="obs-filter-bar__tag">
							<strong>{f.field}</strong>: {f.value}
							<span onClick={() => clearFilter(f.field)} className="obs-filter-bar__tag-x">
								&times;
							</span>
						</span>
					))}
					<span onClick={clearAllFilters} className="obs-filter-bar__clear">
						Clear all
					</span>
				</div>
			)}

			{/* AG Grid */}
			<div style={{ flex: 1, width: '100%' }} className="ag-theme-quartz">
				<AgGridReact
					ref={gridRef}
					rowData={data}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					rowModelType="clientSide"
					onGridReady={onGridReady}
					onFirstDataRendered={onFirstDataRendered}
					onCellValueChanged={onCellValueChanged}
					onFilterChanged={onFilterChanged}
					onSelectionChanged={onSelectionChangedProp ? handleSelectionChanged : undefined}
					onColumnResized={onColumnStateChanged}
					onColumnMoved={onColumnStateChanged}
					onColumnVisible={onColumnStateChanged}
					onSortChanged={onColumnStateChanged}
					animateRows={false}
					suppressAnimationFrame
					enableBrowserTooltips={true}
					rowSelection={rowSelection}
					selectionColumnDef={selectionColumnDef}
					enableRangeSelection={false}
					groupDisplayType="singleColumn"
					rowGroupPanelShow="never"
					sideBar={sideBarConfig}
					theme={theme}
					getRowId={getRowId}
					domLayout="normal"
					suppressColumnVirtualisation={false}
					groupDefaultExpanded={1}
					autoGroupColumnDef={autoGroupColDef}
					suppressAggFuncInHeader={true}
				/>
			</div>
		</div>
	);
};

export default BaseOrderGrid;
